import React from 'react';
import PropTypes from 'prop-types';
import { Table, DropdownItem, TableCellDropdown, ToastContext } from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useSegment } from '../../components/tracking/trackingProvider';
import { usePermissions } from '../../user/permissions';

import { filterCells } from './utils';
import RateTemplate from './../../models/rateTemplate';

import PositionInput from './position-input';

import Styles from '../../styles/rate/rateList.module.css';
import { SeeAttendeesAction } from './components/action/seeAttendeesAction';
import { GroupAction, AddToGroupActionModal } from './components/action/groupAction';
import { InventoryAction, InventoryActionModal } from './components/action/inventoryAction';
import { CategoryAction, CategoryActionModal } from './components/action/categoryAction';
import { DeleteAction } from './components/action/deleteAction';
import DeletionRateModal from './components/action/deleteActionModal';
import { DuplicateAction, DuplicateActionModal } from './components/action/duplicateAction';

const { Tr: TableRow, Td: TableCell } = Table;

export default function RateElement({
    organizationId,
    event,
    rate,
    isChecked,
    onSelected,
    reloadList,
    inventories,
    categories,
    group,
    groups,
    rates,
    GroupClass,
    RateClass,
    tableCells,
    orgaWithChart,
    onPositionChanged = () => {},
    mode,
    getQuotaTakenRate = () => {},
    customActions = null,
    onClick = null
}) {
    const { t } = useTranslation();
    const { track } = useSegment();

    const history = useHistory();
    const permissions = usePermissions();

    const toast = React.useContext(ToastContext);

    const [rateData, setRateData] = React.useState(null);
    const [currentAction, setCurrentAction] = React.useState('');

    const setRatePosition = React.useCallback(
        position => {
            onPositionChanged(rate, position);
        },
        [rate, onPositionChanged]
    );

    React.useEffect(() => {
        let categoryId = rate.category || rate?.rate_template?.category;
        let category = categories ? categories.find(category => category.uuid === categoryId) : null;

        let rateData = {
            event,
            inventories,
            category,
            rates,
            categories,
            groups
        };

        setRateData(rateData);
    }, [event, inventories, categories, rate, rates, groups]);

    //For later when visiblity will be implemented
    //const [visibility, setVisibility] = React.useState(true);
    // const handleClickVisibility = React.useCallback(() => {
    //     rate.visibility = visibility;
    //     rate.save({
    //         organizationId: organizationId,
    //         eventId: event.id()
    //     }).then(({response})=> {
    //         toast.success(t(`ticket.rates.editVisibilitySuccess`));
    //         setVisibility(!visibility);
    //     }).catch(e => {
    //         toast.error(t(`ticket.rates.editVisibilityError`));
    //         setVisibility(!visibility);
    //     });
    // },[visibility]);

    const transformRate = React.useCallback(() => {
        let rate_template = new RateTemplate({
            source_rate_id: rate.pk
        });
        rate_template
            .save({ organizationId })
            .then(() => {
                toast.success(t(`ticket.rates.transformSuccess`));
                reloadList();
            })
            .catch(e => {
                console.error(e);
                toast.error(t(`ticket.rates.transformError`));
            });
        track('ACTION > CREATE RATE TEMPLATE', { 'Page Title': 'TICKET TYPES', 'Action type': 'Unique' });
    }, [rate, event, organizationId]);

    const editRate = React.useCallback(() => {
        track('ACTION > EDIT RATE', { 'Page Title': 'TICKET TYPES', 'Action type': 'Unique' });
        if (event) {
            return history.push(`/ticket/O${organizationId}/E${event.id}/settings/rates/${rate.pk}`);
        }
        return history.push(`/ticket/O${organizationId}/rates/${rate.pk}`);
    }, [rate, event, organizationId]);

    let cells = React.useMemo(() => {
        if (!rateData) {
            return null;
        }
        return tableCells
            .filter(column => filterCells(event, column, orgaWithChart))
            .map((column, index) => (
                <TableCell key={`rate-cell-${index}`} className={column.className} onClick={!column.disabledOnClick && mode === 'rate-list' ? () => editRate() : null}>
                    {column.renderRate(rate, rateData)}
                </TableCell>
            ));
    }, [tableCells, rate, event, orgaWithChart, editRate, mode, rateData]);

    return (
        <React.Fragment>
            {currentAction === 'delete' && (
                <DeletionRateModal
                    objectsToDelete={[rate]}
                    show={currentAction === 'delete'}
                    model={RateClass}
                    rates={rates}
                    onSave={() => {
                        setCurrentAction('');
                        reloadList();
                    }}
                    onClose={() => setCurrentAction('')}
                />
            )}
            {currentAction === 'addToGroup' && (
                <AddToGroupActionModal
                    selectedRates={[rate]}
                    showModal={currentAction === 'addToGroup'}
                    groups={groups}
                    rates={rates}
                    model={RateClass}
                    onSave={() => {
                        reloadList();
                        track('ACTION > MOVE TO GROUP', { 'Page Title': 'TICKET TYPES', 'Action type': 'Unique' });
                    }}
                    onClose={() => setCurrentAction('')}
                />
            )}
            {currentAction === 'inventory' && (
                <InventoryActionModal
                    showModal={currentAction === 'inventory'}
                    selectedRates={[rate]}
                    inventories={inventories}
                    rates={rates}
                    onSave={reloadList}
                    onClose={() => setCurrentAction('')}
                />
            )}
            {currentAction === 'category' && (
                <CategoryActionModal
                    showModal={currentAction === 'category'}
                    selectedRates={[rate]}
                    categories={categories}
                    rates={rates}
                    model={RateClass}
                    onSave={reloadList}
                    onClose={() => setCurrentAction('')}
                />
            )}
            {event && currentAction === 'duplicate' && (
                <DuplicateActionModal
                    showModal={currentAction === 'duplicate'}
                    selectedRates={[rate]}
                    rates={rates}
                    groups={groups}
                    onSave={reloadList}
                    onClose={() => setCurrentAction('')}
                />
            )}
            <TableRow
                disabled={rate.distributor && mode === 'ticket_office'}
                active={isChecked}
                onSelected={(event, data) => onSelected({ event, data }, rate, 'rate')}
                onClick={onClick ? () => onClick(rate) : null}
                withHoverHighlightRow={true}
            >
                {cells}
                {mode === 'resale' && <TableCellDropdown>{customActions(rate)}</TableCellDropdown>}
                {mode === 'rate-list' && (
                    <>
                        <TableCellDropdown
                            onOpen={() => {
                                track('ACTION > CELL ACTIONS', { 'Page Title': 'TICKET TYPES' });
                            }}
                            className={Styles['rate-element-dropdown']}
                        >
                            <SeeAttendeesAction objects={[rate]} model={RateClass} filterKey={event && event.is_seated ? 'booking__rate__in' : 'rate__in'} />
                            <DropdownItem item={t('ticket.rates.modifyRate')} onClick={() => editRate()} />
                            <GroupAction
                                withModal={false}
                                objects={[rate]}
                                groups={groups}
                                rates={rates}
                                model={RateClass}
                                onClick={() => setCurrentAction('addToGroup')}
                                onSave={() => {
                                    reloadList();
                                    track('ACTION > MOVE TO GROUP', { 'Page Title': 'TICKET TYPES', 'Action type': 'Unique' });
                                }}
                            />
                            {(event?.isSeatedForRates() || orgaWithChart) && (
                                <>
                                    <CategoryAction
                                        withModal={false}
                                        objects={[rate]}
                                        categories={categories}
                                        rates={rates}
                                        model={RateClass}
                                        onSave={reloadList}
                                        onClick={() => setCurrentAction('category')}
                                    />
                                    {!event?.use_sessions && (
                                        <InventoryAction
                                            withModal={false}
                                            objects={[rate]}
                                            model={RateClass}
                                            inventories={inventories}
                                            rates={rates}
                                            onSave={reloadList}
                                            onClick={() => setCurrentAction('inventory')}
                                        />
                                    )}
                                </>
                            )}
                            {event && (
                                <>
                                    <DropdownItem.Line />
                                    <DuplicateAction
                                        withModal={false}
                                        objects={[rate]}
                                        rates={rates}
                                        groups={groups}
                                        onClick={() => setCurrentAction('duplicate')}
                                        onSave={() => {
                                            reloadList();
                                            track('ACTION > DUPLICATE RATE', { 'Page Title': 'TICKET TYPES', 'Action type': 'Unique' });
                                        }}
                                    />
                                    {permissions.hasCapacity('PyvarCapacity.RATE_TEMPLATE', organizationId) && (
                                        <DropdownItem item={<span>{t('ticket.rates.transform')}</span>} onClick={transformRate} />
                                    )}
                                </>
                            )}
                            <DropdownItem.Line />
                            <DeleteAction
                                objects={[rate]}
                                rates={rates}
                                model={RateClass}
                                withModal={false}
                                disabled={getQuotaTakenRate(rate) > 0}
                                onClick={() => setCurrentAction('delete')}
                                onSave={() => {
                                    reloadList();
                                    track('ACTION > DELETE RATE', { 'Page Title': 'TICKET TYPES', 'Action type': 'Unique' });
                                }}
                            />
                        </TableCellDropdown>
                        {!event && (
                            <TableCell>
                                <PositionInput
                                    position={rate.display_order}
                                    onUp={() => {
                                        setRatePosition(rate.display_order + 1);
                                    }}
                                    onDown={() => {
                                        setRatePosition(rate.display_order - 1);
                                    }}
                                    min={0}
                                    max={rates.length}
                                />
                            </TableCell>
                        )}
                    </>
                )}
            </TableRow>
        </React.Fragment>
    );
}

RateElement.defaultProps = {
    event: null,
    orgaWithChart: false
};

RateElement.propTypes = {
    rate: PropTypes.object.isRequired,
    event: PropTypes.object,
    organizationId: PropTypes.number.isRequired,
    isChecked: PropTypes.bool.isRequired,
    onSelected: PropTypes.func.isRequired,
    reloadList: PropTypes.func.isRequired,
    categories: PropTypes.array,
    inventories: PropTypes.array,
    group: PropTypes.object,
    rates: PropTypes.array.isRequired,
    RateClass: PropTypes.func.isRequired,
    GroupClass: PropTypes.func.isRequired,
    tableCells: PropTypes.array.isRequired,
    orgaWithChart: PropTypes.bool,
    onPositionChanged: PropTypes.func
};
